import axios from 'axios';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Loader from "../util/Loader";
import './Chart.css';
import { applyConfig } from './ChartHelper';
import ChartFilters from './ChartFilters';

/* eslint-disable react-hooks/exhaustive-deps */

  // helper: overlay scatter series with ** labels at suppressed points
// helper: overlay scatter series with ** labels at suppressed points
export function addSuppressionLabelsOverlay(options, categories, seriesArray) {
  console.log('=== addSuppressionLabelsOverlay START ===');
  console.log('Input categories:', categories);
  console.log('Input seriesArray:', JSON.stringify(seriesArray, null, 2));

  // Always strip any prior overlay series to prevent accumulation/stale overlays
  const baseSeries = (seriesArray || []).filter((s) => s && s.name !== 'Suppressed');

  const suppressedIdx = new Set();

  baseSeries.forEach((s, seriesIndex) => {
    (s.data || []).forEach((v, i) => {
      // Treat only true null/undefined as suppressed.
      // If you later decide to use 0.01 sentinel values, this won't accidentally mark them suppressed.
      if (v === null || typeof v === 'undefined') {
        suppressedIdx.add(i);
        console.log(`Found suppressed value at series ${seriesIndex}, index ${i}`);
      }
    });
  });

  console.log('Suppressed indices:', Array.from(suppressedIdx));

  if (suppressedIdx.size === 0) {
    // Ensure we also remove any lingering "Suppressed" overlay if there are no suppressed points now
    options.series = baseSeries;
    console.log('No suppressed values found; returning options with baseSeries only');
    console.log('=== addSuppressionLabelsOverlay END ===');
    return options;
  }

  const numCategories = (categories || []).length;
  const numSeries = baseSeries.length;
  const isTallData = numSeries > numCategories;

  const yAnchor =
    options.yAxis && options.yAxis.min != null
      ? options.yAxis.min
      : 0;

  if (isTallData) {
    // Keep your existing tall behavior (minimal churn),
    // but apply it ONLY to baseSeries (never to already-mutated options.series).
    const modifiedSeries = baseSeries.map((s) => {
      const modifiedData = (s.data || []).map((v) => {
        if (v === null || typeof v === 'undefined') {
          return {
            y: 0.01,
            dataLabels: {
              enabled: true,
              format: '**',
              style: {
                fontWeight: 'bold',
                color: '#000',
                fontSize: '16px',
                textOutline: 'none'
              },
              verticalAlign: 'top',
              y: 0
            },
            color: 'rgba(0,0,0,0)',
            borderWidth: 0,
            enableMouseTracking: false
          };
        }
        return v;
      });

      return { ...s, data: modifiedData };
    });

    options.series = modifiedSeries;
  } else {
    // Wide data: scatter overlay approach (on top of baseSeries)
    const overlayPoints = Array.from(suppressedIdx).map((i) => ({
      x: i,
      y: yAnchor,
      dataLabels: {
        enabled: true,
        format: '**',
        y: -10,
        style: { fontWeight: 'bold' }
      },
      marker: { enabled: false },
      enableMouseTracking: false
    }));

    const overlaySeries = {
      type: 'scatter',
      name: 'Suppressed',
      data: overlayPoints,
      showInLegend: false,
      enableMouseTracking: false,
      zIndex: 5
    };

    options.series = [...baseSeries, overlaySeries];
  }

  console.log('Final options.series:', JSON.stringify(options.series, null, 2));
  console.log('=== addSuppressionLabelsOverlay END ===');

  return options;
}


export default function ColumnChartFiltered() {
  Highcharts.setOptions({
    lang: { thousandsSep: ',' }
  });
    
  const [uploadType, setUploadType] = useState('');
  const [options, setOptions] = useState({});
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [filterConfig, setFilterConfig] = useState({ filters: [] });
  const [filterOptions, setFilterOptions] = useState({});
  const baseUri = process.env.REACT_APP_API_URL;
  const [chartKey, setChartKey] = useState(0);

  const showModal = () => setOpen(true);
  const hideModal = () => setOpen(false);

  console.log(uploadType)

  function generateDefaultQueryString(filterOptions) {
    const queryString = Object.keys(filterOptions)
      .map((key) => {
        const filterData = filterOptions[key];
        // Use default value if provided, otherwise "-- All --"
        const defaultValue = !Array.isArray(filterData) && filterData?.default
          ? filterData.default
          : "-- All --";
        return `${key}=${encodeURIComponent(defaultValue)}`;
      })
      .join("&");
    return `?${queryString}`;
  }



const data = useCallback(async () => {
  showModal();
  const chartId = id;
  console.log('CHART ID', chartId);
  const newChartType = chartId.includes('line') ? 'line' : 'column';

  // get available filters
  const resp = await axios.get(`${baseUri}/chart/filters/${chartId}`);
  const chartFilters = resp.data;

  const filters = generateDefaultQueryString(chartFilters);

  // get chart data
  const response = await axios.get(`${baseUri}/chart/bar/${chartId}${filters}`);
  const chartConfig = response.data.metadata.config;
  setFilterConfig(chartConfig);
  setFilterOptions(chartFilters);

  setUploadType(response.data.metadata?.uploadType);
  const uploadLink = baseUri + '/download/' + response.data.metadata?.uploadType + filters;

  // IMPORTANT: Deep-clone the raw series so any mutations we do (suppression overlays)
  // cannot leak across subsequent filter/reset operations.
  const rawSeries =
    typeof structuredClone === 'function'
      ? structuredClone(response.data.series)
      : JSON.parse(JSON.stringify(response.data.series));

  let options = {
    chart: { type: newChartType },
    title: { text: '' },
    subtitle: {
      text: 'Note: ** indicates suppressed values due to small sample sizes'
    },
    series: rawSeries, // suppressed values remain as null
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0
      },
      series: {
        connectNulls: false,
        dataLabels: {
          enabled: true,
          formatter: function () {
            if (this.y === null || typeof this.y === 'undefined') {
              return '**';
            }
            return Highcharts.numberFormat(this.y, 1) + '%';
          }
        }
      }
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const name = `<b>${this.series.name}</b>`;
        const x = `<span>${this.x}</span>`;
        if (this.y === null || typeof this.y === 'undefined') {
          return `${name}<br/>${x}: <b>** (Suppressed)</b>`;
        }
        return `${name}<br/>${x}: <b>${Highcharts.numberFormat(this.y, 1)}%</b>`;
      }
    },
    xAxis: {
      categories: response.data.categories,
      crosshair: true
    },
    yAxis: {
      min: 0,
      title: { text: '' },
      labels: { format: '{value:.1f}%' },
      type: 'percent'
    }
  };

  options = await applyConfig(options, response.data.metadata.config, 'ColumnChart');

  if (uploadLink) {
    let linkStyle = { fontSize: '14px' };
    options.title.useHTML = true;
    options.title.style = linkStyle;
    options.title.text = `<a href="${uploadLink}">Download full dataset</a>`;
  }

  // IMPORTANT: Run suppression overlay against the RAW response series (not options.series),
  // so we never re-process already-mutated series objects.
  options = addSuppressionLabelsOverlay(options, response.data.categories, rawSeries);

  setOptions(options);
  setChartKey((k) => k + 1);
  hideModal();
}, [id]);


  useEffect(() => {
    data();
  }, [data]);

  return (
    <div>
      <Loader open={open} />
      <ChartFilters
        filterConfig={filterConfig}
        filterOptions={filterOptions}
        baseUri={baseUri}
        chartId={id}
        options={options}
        setOptions={setOptions}
      />
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        key={chartKey}
         immutable={true}
      />
    </div>
  );
}






